/**
 * Problem Generator
 * Problem generation for all four math operations + word practice
 * IMPORTANT: Algorithms must be preserved exactly - do not modify
 */

import { DEFAULT_SETTINGS } from '@config/settings'
import { OPERATION_LIMITS, MULTIPLE_CHOICE } from '@config/constants'
import { sanitizeMaxValue } from './validation'
import { WORD_PRACTICE_BANK as IMPORTED_WORD_BANK } from '@data/wordBanks'
import { LETTERS_ITEMS } from '@data/letters'
import type { Settings, Problem } from '../types'
import type { ProblemMetadata } from '../types/problem'

export type OperationId =
  | 'multiplication'
  | 'division'
  | 'addition'
  | 'subtraction'
  | 'wordPractice'
  | 'letters'

export interface ProblemWithOperation extends Problem {
  operationId?: OperationId
}

// Import word practice data
export const WORD_PRACTICE_BANK = IMPORTED_WORD_BANK
export const ALL_WORD_PRACTICE_WORDS = Array.from(new Set(Object.values(WORD_PRACTICE_BANK).flat()))
export const LETTERS_WORDS = LETTERS_ITEMS.map(item => item.word)

export function randInt(min: number, max: number): number {
  return Math.floor(Math.random() * (max - min + 1)) + min
}

export function shuffleArray<T>(list: T[]): T[] {
  const array = [...list]
  for (let i = array.length - 1; i > 0; i -= 1) {
    const j = Math.floor(Math.random() * (i + 1))
    ;[array[i], array[j]] = [array[j], array[i]]
  }
  return array
}

function ensureSettings(settings?: Partial<Settings>): Settings {
  return (settings as Settings) || DEFAULT_SETTINGS
}

export function generateMultiplicationProblem(settings?: Partial<Settings>): Problem {
  const currentSettings = ensureSettings(settings)
  const maxMultiplicand = sanitizeMaxValue(
    currentSettings?.maxValues?.multiplication,
    DEFAULT_SETTINGS.maxValues.multiplication,
    OPERATION_LIMITS.MULTIPLICATION.min,
    OPERATION_LIMITS.MULTIPLICATION.max
  )
  const a = randInt(0, maxMultiplicand)
  const b = randInt(0, maxMultiplicand)
  return {
    prompt: `${a} × ${b}`,
    answer: a * b,
    metadata: { operands: [a, b] },
  }
}

export function generateDivisionProblem(settings?: Partial<Settings>): Problem {
  const currentSettings = ensureSettings(settings)
  const maxDivision = sanitizeMaxValue(
    currentSettings?.maxValues?.division,
    DEFAULT_SETTINGS.maxValues.division,
    OPERATION_LIMITS.DIVISION.min,
    OPERATION_LIMITS.DIVISION.max
  )
  const divisor = randInt(1, maxDivision)
  const quotient = randInt(1, maxDivision)
  const dividend = divisor * quotient
  return {
    prompt: `${dividend} ÷ ${divisor}`,
    answer: quotient,
    metadata: { operands: [dividend, divisor], quotient },
  }
}

export function generateAdditionProblem(settings?: Partial<Settings>): Problem {
  const currentSettings = ensureSettings(settings)
  const maxAddition = sanitizeMaxValue(
    currentSettings?.maxValues?.addition,
    DEFAULT_SETTINGS.maxValues.addition,
    OPERATION_LIMITS.ADDITION.min,
    OPERATION_LIMITS.ADDITION.max
  )
  const a = randInt(0, maxAddition)
  const b = randInt(0, maxAddition)
  return {
    prompt: `${a} + ${b}`,
    answer: a + b,
    metadata: { operands: [a, b] },
  }
}

export function generateSubtractionProblem(settings?: Partial<Settings>): Problem {
  const currentSettings = ensureSettings(settings)
  const maxSubtraction = sanitizeMaxValue(
    currentSettings?.maxValues?.subtraction,
    DEFAULT_SETTINGS.maxValues.subtraction,
    OPERATION_LIMITS.SUBTRACTION.min,
    OPERATION_LIMITS.SUBTRACTION.max
  )
  const minuend = randInt(0, maxSubtraction)
  const subtrahend = randInt(0, minuend)
  return {
    prompt: `${minuend} − ${subtrahend}`,
    answer: minuend - subtrahend,
    metadata: { operands: [minuend, subtrahend] },
  }
}

export function generateWordPracticeProblem(settings?: Partial<Settings>): Problem {
  const currentSettings = ensureSettings(settings)
  const difficulty = currentSettings.wordPractice?.difficulty || 1
  const bank =
    WORD_PRACTICE_BANK[difficulty] ||
    WORD_PRACTICE_BANK[DEFAULT_SETTINGS.wordPractice.difficulty] ||
    []
  if (bank.length === 0) {
    return { prompt: 'cat', answer: 'cat' } // Fallback
  }
  const word = bank[randInt(0, bank.length - 1)]

  // Levels 1-5: Show word, child reads it
  // Levels 6-10: Speak word (tap to hear), child spells it
  if (difficulty >= 6) {
    return {
      prompt: `🔊 Tap to hear the word`,
      answer: word,
      metadata: { word, mode: 'spelling' }, // Store word for TTS
    }
  } else {
    return {
      prompt: word,
      answer: word,
      metadata: { mode: 'reading' },
    }
  }
}

function buildLettersPattern(word: string, level: number) {
  const safeWord = word.toLowerCase()
  const letters = safeWord.split('')
  if (letters.length < 3) {
    return { pattern: safeWord, answer: safeWord }
  }

  if (level === 1) {
    return { pattern: `_${safeWord.slice(1)}`, answer: letters[0] }
  }

  if (level === 2) {
    const middle = letters[1] ?? ''
    return { pattern: `${letters[0] ?? ''}_${letters[2] ?? ''}`, answer: middle }
  }

  return { pattern: '_'.repeat(letters.length), answer: safeWord }
}

export function generateLettersProblem(settings?: Partial<Settings>): Problem {
  const currentSettings = ensureSettings(settings)
  const level = currentSettings.lettersPractice?.level ?? DEFAULT_SETTINGS.lettersPractice.level
  const bank = LETTERS_ITEMS.length > 0 ? LETTERS_ITEMS : [{ word: 'cat', Svg: () => null }]
  const item = bank[randInt(0, bank.length - 1)]
  const { pattern, answer } = buildLettersPattern(item.word, level)

  return {
    prompt: pattern,
    answer,
    metadata: {
      word: item.word,
      lettersLevel: level,
      lettersPattern: pattern,
      promptKey: `letters-${item.word}-${level}-${pattern}`,
    },
  }
}

function generateWordPracticeChoices(
  problem: ProblemWithOperation,
  settings?: Partial<Settings>
): (number | string)[] {
  const currentSettings = ensureSettings(settings)
  const difficulty = currentSettings.wordPractice?.difficulty || 1
  const bank =
    WORD_PRACTICE_BANK[difficulty] ||
    WORD_PRACTICE_BANK[DEFAULT_SETTINGS.wordPractice.difficulty] ||
    []
  const correct = String(problem.answer)
  const primaryPool = bank.filter(word => word !== correct)
  const fallbackPool = ALL_WORD_PRACTICE_WORDS.filter(word => word !== correct)
  const combined = [...primaryPool, ...fallbackPool.filter(word => !primaryPool.includes(word))]
  const distractors = shuffleArray(combined).slice(0, 3)
  while (distractors.length < 3) {
    const randomWord = ALL_WORD_PRACTICE_WORDS[randInt(0, ALL_WORD_PRACTICE_WORDS.length - 1)]
    if (randomWord !== correct && !distractors.includes(randomWord)) {
      distractors.push(randomWord)
    }
  }
  return shuffleArray([correct, ...distractors.slice(0, 3)])
}

export function getAnswerBoundsForOperation(
  operationId: OperationId,
  settings?: Partial<Settings>
): { min: number; max: number } {
  const currentSettings = ensureSettings(settings)
  const maxValues = currentSettings.maxValues || DEFAULT_SETTINGS.maxValues
  const bounds: { min: number; max: number } = {
    min: MULTIPLE_CHOICE.DEFAULT_MIN_BOUND,
    max: MULTIPLE_CHOICE.DEFAULT_MAX_BOUND,
  }

  switch (operationId) {
    case 'multiplication': {
      const maxFactor = sanitizeMaxValue(
        maxValues.multiplication,
        DEFAULT_SETTINGS.maxValues.multiplication,
        OPERATION_LIMITS.MULTIPLICATION.min,
        OPERATION_LIMITS.MULTIPLICATION.max
      )
      bounds.max = Math.max(MULTIPLE_CHOICE.MIN_REASONABLE_BOUND, maxFactor * maxFactor)
      break
    }
    case 'division': {
      const maxDivision = sanitizeMaxValue(
        maxValues.division,
        DEFAULT_SETTINGS.maxValues.division,
        OPERATION_LIMITS.DIVISION.min,
        OPERATION_LIMITS.DIVISION.max
      )
      bounds.max = Math.max(MULTIPLE_CHOICE.MIN_REASONABLE_BOUND, maxDivision)
      break
    }
    case 'addition': {
      const maxAddition = sanitizeMaxValue(
        maxValues.addition,
        DEFAULT_SETTINGS.maxValues.addition,
        OPERATION_LIMITS.ADDITION.min,
        OPERATION_LIMITS.ADDITION.max
      )
      bounds.max = Math.max(MULTIPLE_CHOICE.MIN_REASONABLE_BOUND, maxAddition * 2)
      break
    }
    case 'subtraction': {
      const maxSubtraction = sanitizeMaxValue(
        maxValues.subtraction,
        DEFAULT_SETTINGS.maxValues.subtraction,
        OPERATION_LIMITS.SUBTRACTION.min,
        OPERATION_LIMITS.SUBTRACTION.max
      )
      bounds.max = Math.max(MULTIPLE_CHOICE.MIN_REASONABLE_BOUND, maxSubtraction)
      break
    }
    default:
      bounds.max = MULTIPLE_CHOICE.DEFAULT_MAX_BOUND
  }

  return bounds
}

export function generateMultipleChoiceOptions(
  problem: ProblemWithOperation,
  settings?: Partial<Settings>
): (number | string)[] {
  if (problem.operationId === 'letters') {
    return []
  }
  if (problem.operationId === 'wordPractice') {
    return generateWordPracticeChoices(problem, settings)
  }

  const correct = problem.answer as number
  const { min, max } = getAnswerBoundsForOperation(problem.operationId!, settings)
  const choices = new Set<number>([correct])

  const addCandidate = (candidate: number) => {
    if (choices.size >= MULTIPLE_CHOICE.OPTIONS_COUNT) return
    const clamped = Math.max(min, Math.min(max, candidate))
    if (!choices.has(clamped)) {
      choices.add(clamped)
    }
  }

  // Neighborhood offsets based on problem magnitude
  const rangeSpan = Math.max(MULTIPLE_CHOICE.OPTIONS_COUNT, max - min)
  const offsetRange = Math.max(
    MULTIPLE_CHOICE.MIN_OFFSET_RANGE,
    Math.round(rangeSpan * MULTIPLE_CHOICE.OFFSET_RATIO)
  )
  const baseOffsets = [1, -1, 2, -2, Math.max(3, Math.round(offsetRange * 0.4)), -Math.max(3, Math.round(offsetRange * 0.4))]

  baseOffsets.forEach(offset => addCandidate(correct + offset))

  // Common slip patterns
  const metadata = problem.metadata as ProblemMetadata | undefined
  if (metadata?.operands && Array.isArray(metadata.operands)) {
    const [a, b] = metadata.operands
    // Operand echo
    addCandidate(a)
    addCandidate(b)
    // Off-by-operand slips
    addCandidate(correct + b)
    addCandidate(correct - b)

    if (problem.operationId === 'multiplication') {
      const bumpB = a * Math.max(0, b + 1)
      const dropB = a * Math.max(0, b - 1)
      addCandidate(bumpB)
      addCandidate(dropB)
    }

    if (problem.operationId === 'division') {
      const divisor = b
      const dividend = a
      addCandidate(divisor)
      addCandidate(dividend)
      if (divisor > 1) {
        addCandidate(Math.floor(dividend / (divisor - 1)))
      }
      addCandidate(Math.floor(dividend / (divisor + 1)))
    }
  }

  // Fill remaining with randomized nearby values
  let attempts = 0
  while (choices.size < MULTIPLE_CHOICE.OPTIONS_COUNT && attempts < MULTIPLE_CHOICE.MAX_GENERATION_ATTEMPTS) {
    const jitter = randInt(-offsetRange, offsetRange)
    addCandidate(correct + jitter)
    attempts += 1
  }

  // As a fallback, fill symmetrically outward
  let filler = 1
  while (choices.size < MULTIPLE_CHOICE.OPTIONS_COUNT && filler < MULTIPLE_CHOICE.FILLER_LIMIT) {
    addCandidate(correct + filler)
    addCandidate(correct - filler)
    filler += 1
  }

  while (choices.size < MULTIPLE_CHOICE.OPTIONS_COUNT) {
    choices.add(randInt(min, max + filler))
    filler += 1
  }

  return shuffleArray(Array.from(choices))
}
